import datapoints from '../datapoints';
import store from '../store/parentStore';
import WindowFeaturesOptions from '../store/schema/WindowFeaturesOptions';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getPopoutUrl } from 'owa-url';
import {
    POPOUT_V2_QUERY,
    POPOUT_V2_VALUE,
    POPOUT_LEAN_BOOTSTRAP_QUERY,
    POPOUT_LEAN_BOOTSTRAP_VAULE,
    DEFAULT_POPOUT_MIN_HEIGHT,
    DEFAULT_POPOUT_MIN_WIDTH,
    CASCADE_POPOUT_LOOP_AFTER_COUNT,
    CASCADE_POPOUT_EVERY_OFFSET,
} from '../utils/constants';
import { getClientVersion } from 'owa-config';
import { LazyModule, LazyImport } from 'owa-bundling';
import { isFeatureEnabled } from 'owa-feature-flags';

const VERSION = 'version';
const DEFAULT_WINDOW_FEATURES_OPTIONS: Partial<WindowFeaturesOptions> = {
    location: false,
    menubar: false,
    resizable: true,
    scrollbars: false,
    status: false,
    toolbar: false,
};
const lazyModule = new LazyModule(() => import(/* webpackChunkName: "Popout" */ '../lazyIndex'));
const lazyHandleMessageFromParentWindow = new LazyImport(
    lazyModule,
    m => m.handleMessageFromParentWindow
);

// start counter at -1 because we will immediately add 1 later
let nextWindowPositionCounter = -1;

/**
 * open a new popout with the given vdir, route and data.
 * @param vdir mail or calendar
 * @param route the route path of the popout window, e.g. 'compose', 'read'
 * @param data a data object or a callback which returns a promise of the data object
 * @param options: window options, such as width, height, ...
 * @param skipOptinCheck Whether skip optIn check. When set to true, a param "minus" will be added to the url actionSource: Source of this action
 * @param actionSource Source of this action
 */
export default createLegacyActionWithDatapoint('addPopoutV2', datapoints.PopoutAddPopout)(function(
    vdir: 'mail' | 'calendar',
    route: string,
    data?: Object | (() => Promise<Object>),
    options?: Partial<WindowFeaturesOptions>,
    skipOptinCheck?: boolean,
    actionSource?: string
) {
    if (!store.popouts) {
        lazyHandleMessageFromParentWindow.import();
        window.addEventListener('message', onMessage);
        store.popouts = [];
    }

    const parameters: Record<string, string> = {
        [VERSION]: getClientVersion(),
        [POPOUT_V2_QUERY]: POPOUT_V2_VALUE, // Use this parameter to tell popout window that it is opened from OWA so possibly there are some data to retrieve from parent window
    };

    if (isFeatureEnabled('rp-popout-lean-bootstrap')) {
        parameters[POPOUT_LEAN_BOOTSTRAP_QUERY] = POPOUT_LEAN_BOOTSTRAP_VAULE;
    }

    const popoutUrl = getPopoutUrl(vdir, route, skipOptinCheck, parameters);

    try {
        const childWindow = window.open(popoutUrl, '_blank', generateWindowFeaturesString(options));

        const dataCallback =
            typeof data == 'function'
                ? (data as () => Promise<Object>)
                : () => Promise.resolve(data);
        store.popouts.push({
            window: childWindow,
            dataCallback,
        });
    } catch {
        // TODO: Work Item 39263: [PopoutV2] Consider how to handle blocked scenario for popout V2
    }
});

/**
 * Generate the string of new window features. e.g.: width=800,height=600,menubar=0,...
 * @param optionsProp The properties to override default values
 */
function generateWindowFeaturesString(optionsProp: Partial<WindowFeaturesOptions>) {
    function valueOf(value: number | boolean | undefined): string | undefined {
        if (typeof value === 'boolean') {
            return value ? '1' : '0';
        } else if (value) {
            return String(value);
        } else {
            return undefined;
        }
    }

    const anchoredOffset = getNextAnchoredOffset();
    const anchoredSizingOptions: Partial<WindowFeaturesOptions> = {
        // always relative to main window, and cascades for comfort
        left: window.screenX + anchoredOffset,
        top: window.screenY + anchoredOffset,
        // empirically-determined to be comfortable-sized
        height: Math.max(25 * Math.pow(window.innerHeight, 1 / 2), DEFAULT_POPOUT_MIN_HEIGHT),
        width: Math.max(8 * Math.pow(window.innerWidth, 2 / 3), DEFAULT_POPOUT_MIN_WIDTH),
    };

    const options = {
        ...DEFAULT_WINDOW_FEATURES_OPTIONS,
        ...anchoredSizingOptions,
        ...optionsProp,
    };

    return (Object.getOwnPropertyNames(options) as (keyof WindowFeaturesOptions)[])
        .map((key: keyof WindowFeaturesOptions) => `${key}=${valueOf(options[key])}`)
        .join(',');
}

function onMessage(e: MessageEvent) {
    lazyHandleMessageFromParentWindow
        .import()
        .then(handleMessageFromParentWindow => handleMessageFromParentWindow(e));
}

function getNextAnchoredOffset() {
    nextWindowPositionCounter = (nextWindowPositionCounter + 1) % CASCADE_POPOUT_LOOP_AFTER_COUNT;
    return CASCADE_POPOUT_EVERY_OFFSET * (nextWindowPositionCounter + 1);
}
